import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import JSZip from 'jszip';
import {
  AspectRatio,
  Box,
  VStack,
  HStack,
  Grid,
  Tooltip,
  Image,
  Text,
  Button,
  Checkbox,
  Badge,
  useToast,
  Input,
  Select,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Spinner,
  Center,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogFooter,
} from '@chakra-ui/react';
import {
  Download,
  ImageSquare,
  Tag,
  TrashSimple,
  CaretDown,
  Pencil,
  Upload,
} from '@phosphor-icons/react';
import { adminFunctions } from '../../firebase/adminFunctions';
import BoundingBoxDrawer from '../media/BoundingBoxDrawer';
import DetectionBoxes from '../media/DetectionBoxes';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ImagePreviewModal from '../ImagePreviewModal';
import { exportYoloDataset, exportCocoDataset, downloadAnnotatedImages } from '../../utils/modelDatasetExport';
import DatasetValidator from './model-training/DatasetValidator';

export default function ModelTrainingManager() {
  const [media, setMedia] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('yolo');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ completed: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnnotated, setFilterAnnotated] = useState('all');
  const [currentImage, setCurrentImage] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loadedImageUrl, setLoadedImageUrl] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({});
  const [downloadErrors, setDownloadErrors] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isImagePreviewOpen, onOpen: onImagePreviewOpen, onClose: onImagePreviewClose } = useDisclosure();
  const { isOpen: isErrorDialogOpen, onOpen: openErrorDialog, onClose: closeErrorDialog } = useDisclosure();
  
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOverlaySize, setPreviewOverlaySize] = useState({ width: 0, height: 0 });
  const toast = useToast();
  const cancelRef = useRef();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFeature(null);
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const mediaData = await adminFunctions.getAllMedia();
      const processedMedia = mediaData
        .filter(item => item.url && item.siteId)
        .map(item => ({
          ...item,
          filename: item.filename || item.name || 'Untitled',
          annotations: [
            ...(item.annotations || []),
            ...(item.predictions?.map(pred => ({
              class: pred.class,
              x: pred.x || 0,
              y: pred.y || 0,
              width: pred.width || 0,
              height: pred.height || 0
            })) || [])
          ]
        }));

      const featuresData = await adminFunctions.getAllFeatures();
      const processedFeatures = Object.entries(featuresData)
        .map(([id, feature]) => ({
          ...feature,
          id
        }))
        .filter(feature => feature.active);

      setMedia(processedMedia);
      setFeatures(processedFeatures);

      if (processedMedia.length === 0) {
        toast({
          title: 'No media found',
          description: 'Please upload some media files first',
          status: 'info',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error loading data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBoxDrawn = async (box) => {
    if (!currentImage || !selectedFeature) {
      toast({
        title: 'No feature selected',
        description: 'Please select a feature before drawing a bounding box',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      const displayToNaturalRatio = {
        x: imageSize.naturalWidth / imageSize.width,
        y: imageSize.naturalHeight / imageSize.height
      };

      const yoloBox = {
        class: selectedFeature.id,
        x: Math.max(0, Math.min(1, (box.x * displayToNaturalRatio.x) / imageSize.naturalWidth)),
        y: Math.max(0, Math.min(1, (box.y * displayToNaturalRatio.y) / imageSize.naturalHeight)),
        width: Math.max(0, Math.min(1, (box.width * displayToNaturalRatio.x) / imageSize.naturalWidth)),
        height: Math.max(0, Math.min(1, (box.height * displayToNaturalRatio.y) / imageSize.naturalHeight))
      };

      const updatedImage = {
        ...currentImage,
        annotations: [...(currentImage.annotations || []), yoloBox]
      };

      await adminFunctions.updateMedia(currentImage.id, {
        annotations: updatedImage.annotations
      });

      setCurrentImage(updatedImage);
      setMedia(prev => prev.map(m => 
        m.id === currentImage.id ? updatedImage : m
      ));

      toast({
        title: 'Annotation saved',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error saving annotation',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleAnnotationDelete = async (index) => {
    if (!currentImage) return;

    try {
      const newAnnotations = currentImage.annotations.filter((_, i) => i !== index);
      const updatedImage = {
        ...currentImage,
        annotations: newAnnotations
      };

      await adminFunctions.updateMedia(currentImage.id, {
        annotations: newAnnotations
      });

      setCurrentImage(updatedImage);
      setMedia(prev => prev.map(m => 
        m.id === currentImage.id ? updatedImage : m
      ));

      toast({
        title: 'Annotation deleted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error deleting annotation',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleImageLoad = (imageId, naturalWidth, naturalHeight) => {
    setImageDimensions(prev => ({
      ...prev,
      [imageId]: { width: naturalWidth, height: naturalHeight }
    }));
  };

  const downloadDataset = async (downloadType) => {
    setDownloading(true);
    setDownloadProgress({ completed: 0, total: 0 });

    try {
      // Get all images with annotations
      const annotatedImages = media.filter(item => item.annotations?.length > 0);

      if (annotatedImages.length === 0) {
        setDownloading(false);
        toast({
          title: 'No annotated images',
          description: 'Please annotate some images before downloading.',
          status: 'warning',
          duration: 5000,
        });
        return;
      }

      setDownloadProgress({ completed: 0, total: annotatedImages.length });

      // Ensure we have image dimensions for all annotated images
      const imagesWithDimensions = annotatedImages.map(img => ({
        ...img,
        width: imageDimensions[img.id]?.width || 0,
        height: imageDimensions[img.id]?.height || 0
      }));

      let result = { zip: null, errors: [] };
      let filename = '';
      let toastTitle = '';
      let toastDescription = '';

      const updateProgress = (completed) => {
        setDownloadProgress(prev => ({ ...prev, completed }));
      };

      if (downloadType === 'images') {
        result = await downloadAnnotatedImages(imagesWithDimensions, updateProgress);
        filename = 'annotated_images.zip';
        toastTitle = 'Images Downloaded';
        toastDescription = `Downloaded ${imagesWithDimensions.length - (result.errors?.length || 0)} of ${imagesWithDimensions.length} images`;
      } else {
        let blob;
        if (selectedFormat === 'yolo') {
          blob = await exportYoloDataset(imagesWithDimensions, features, true, updateProgress);
          filename = 'yolo_dataset.zip';
        } else {
          blob = await exportCocoDataset(imagesWithDimensions, features, true, updateProgress);
          filename = 'coco_dataset.zip';
        }
        result = { zip: blob, errors: [] };
        toastTitle = 'Dataset Downloaded';
        toastDescription = `Exported ${imagesWithDimensions.reduce((sum, img) => sum + (img.annotations?.length || 0), 0)} annotations in ${selectedFormat.toUpperCase()} format`;
      }

      const url = window.URL.createObjectURL(result.zip);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: toastTitle,
        description: toastDescription,
        status: result.errors?.length ? 'warning' : 'success',
        duration: 5000,
      });

      if (result.errors?.length) {
        setDownloadErrors(result.errors);
        openErrorDialog();
      }

    } catch (error) {
      console.error('Error downloading:', error);
      toast({
        title: 'Error downloading files',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDownloading(false);
    }
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.filename?.toLowerCase().includes(searchTerm.toLowerCase());
    const hasAnnotations = item.annotations?.length > 0;

    if (filterAnnotated === 'annotated') return matchesSearch && hasAnnotations;
    if (filterAnnotated === 'unannotated') return matchesSearch && !hasAnnotations;
    return matchesSearch;
  });

  const stats = {
    total: media.length,
    annotated: media.filter(item => item.annotations?.length > 0).length,
    annotations: media.reduce((acc, item) => acc + (item.annotations?.length || 0), 0),
    features: new Set(media.flatMap(item => 
      item.annotations?.map(ann => ann.class) || []
    )).size
  };

  return (
    <Box p={4}>
      <VStack spacing={6} align="stretch">
        <Text fontSize="2xl" fontWeight="bold">Model Training Manager</Text>
        
        <Tabs variant="enclosed" colorScheme="blue" onChange={setActiveTab} index={activeTab}>
          <TabList>
            <Tab>Create Annotations</Tab>
            <Tab>Validate Dataset</Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0} pt={4}>
              <VStack spacing={6} align="stretch">
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <HStack spacing={4}>
                      <Select
                        value={selectedFormat}
                        onChange={(e) => setSelectedFormat(e.target.value)}
                        maxW="150px"
                      >
                        <option value="yolo">YOLO</option>
                        <option value="coco">COCO</option>
                      </Select>
                      <Tooltip label="Download dataset with annotations and images">
                        <Button
                          leftIcon={<Download size={20} />}
                          colorScheme="blue"
                          onClick={() => downloadDataset('annotations')}
                          isDisabled={stats.annotations === 0 || downloading}
                          isLoading={downloading}
                          loadingText={`Downloading... ${downloadProgress.completed}/${downloadProgress.total}`}
                        >
                          Download Dataset
                        </Button>
                      </Tooltip>
                      <Tooltip label="Download only annotated images">
                        <Button
                          leftIcon={<ImageSquare size={20} />}
                          colorScheme="green"
                          onClick={() => downloadDataset('images')}
                          isDisabled={stats.annotations === 0 || downloading}
                          isLoading={downloading}
                          loadingText={`Downloading... ${downloadProgress.completed}/${downloadProgress.total}`}
                        >
                          Download Images
                        </Button>
                      </Tooltip>
                    </HStack>
                  </HStack>
                  <Text color="gray.500" fontSize="sm">
                    Create and manage bounding box annotations for your images. Annotations can be downloaded in {selectedFormat === 'yolo' ? 'YOLO' : 'COCO'} format. 
                    {selectedFormat === 'yolo' && (
                      <>
                        YOLO format includes normalized coordinates (values between 0-1) centered at the box middle.
                      </>
                    )}
                    You can download images separately to split the process into smaller parts.
                  </Text>
                </VStack>

                {downloading && (
                  <Progress 
                    value={(downloadProgress.completed / downloadProgress.total) * 100} 
                    size="sm" 
                    colorScheme="blue"
                    hasStripe
                    isAnimated
                  />
                )}

                <StatGroup>
                  <Stat>
                    <StatLabel>Total Images</StatLabel>
                    <StatNumber>{stats.total}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Annotated Images</StatLabel>
                    <StatNumber>{stats.annotated}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Total Annotations</StatLabel>
                    <StatNumber>{stats.annotations}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Unique Features</StatLabel>
                    <StatNumber>{stats.features}</StatNumber>
                  </Stat>
                </StatGroup>

                <HStack spacing={4}>
                  <Input
                    placeholder="Search images..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    maxW="300px"
                  />
                  <Select
                    value={filterAnnotated}
                    onChange={(e) => setFilterAnnotated(e.target.value)}
                    maxW="200px"
                  >
                    <option value="all">All Images</option>
                    <option value="annotated">Annotated</option>
                    <option value="unannotated">Unannotated</option>
                  </Select>
                </HStack>

                <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={6}>
                  {loading ? (
                    <Center gridColumn="1/-1" py={8}>
                      <Spinner size="xl" />
                    </Center>
                  ) : filteredMedia.length === 0 ? (
                    <Center gridColumn="1/-1" py={8}>
                      <Text>No media found</Text>
                    </Center>
                  ) : filteredMedia.map((item) => (
                    <Box
                      key={item.id}
                      borderWidth="1px"
                      borderRadius="lg"
                      overflow="hidden"
                      position="relative"
                    >
                      <AspectRatio ratio={16/9}>
                        <>
                          <Image
                            src={item.url}
                            alt={item.filename}
                            objectFit="cover"
                            onClick={() => {
                              setPreviewImage(item);
                              onImagePreviewOpen();
                            }}
                            cursor="pointer"
                            onLoad={e => {
                              handleImageLoad(item.id, e.target.naturalWidth, e.target.naturalHeight);
                            }}
                            fallback={
                              <Center position="absolute" inset="0" bg="gray.100">
                                <ImageSquare size={32} />
                              </Center>
                            }
                          />
                          {item.annotations?.length > 0 && (
                            <Box position="absolute" inset="0" pointerEvents="none">
                              {item.annotations.map((ann, idx) => (
                                <Box
                                  key={idx}
                                  position="absolute"
                                  top={`${ann.y * 100}%`}
                                  left={`${ann.x * 100}%`}
                                  width={`${ann.width * 100}%`}
                                  height={`${ann.height * 100}%`}
                                  transform="translate(-50%, -50%)"
                                  border="2px solid"
                                  borderColor="blue.500"
                                  backgroundColor="transparent"
                                  pointerEvents="none"
                                  zIndex={2}
                                />
                              ))}
                            </Box>
                          )}
                          <Box position="absolute" top={2} right={2}>
                            <HStack spacing={2}>
                              <IconButton
                                icon={<Pencil />}
                                size="sm"
                                colorScheme="blue"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentImage(item);
                                  onOpen();
                                }}
                                aria-label="Edit annotations"
                              />
                              {item.annotations?.length > 0 && (
                                <Badge colorScheme="green">
                                  {item.annotations.length} annotations
                                </Badge>
                              )}
                            </HStack>
                          </Box>
                        </>
                      </AspectRatio>
                      <Box p={2}>
                        <Text fontSize="sm" noOfLines={1}>{item.filename}</Text>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              </VStack>
            </TabPanel>

            <TabPanel p={0} pt={4}>
              <Suspense fallback={<Center py={8}><Spinner size="xl" /></Center>}>
                <DatasetValidator />
              </Suspense>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Modal 
          isOpen={isOpen} 
          onClose={onClose} 
          size="6xl"
          closeOnOverlayClick={false}
        >
          <ModalOverlay />
          <ModalContent maxW="95vw" maxH="95vh">
            <ModalHeader>Edit Annotations</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {currentImage && (
                <Flex gap={4}>
                  <Box flex="1" position="relative" maxH="70vh">
                    <Box
                      position="relative"
                      width="100%"
                      height="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                    >
                      <Image
                        src={currentImage.url}
                        alt={currentImage.filename}
                        maxH="100%"
                        maxW="100%"
                        style={{ objectFit: "contain" }}
                        onLoad={(e) => {
                          const img = e.target;
                          const rect = img.getBoundingClientRect();
                          setImageSize({
                            width: rect.width,
                            height: rect.height,
                            naturalWidth: img.naturalWidth,
                            naturalHeight: img.naturalHeight
                          });
                          setLoadedImageUrl(currentImage.url);
                        }}
                        fallback={
                          <Center bg="gray.100" p={8}>
                            <Spinner />
                          </Center>
                        }
                      />
                    </Box>
                    {currentImage.annotations?.length > 0 && loadedImageUrl === currentImage.url && (
                      <Box position="absolute" inset="0" pointerEvents="none">
                        {currentImage.annotations.map((ann, idx) => (
                          <Box
                            key={idx}
                            position="absolute"
                            top={`${ann.y * 100}%`}
                            left={`${ann.x * 100}%`}
                            width={`${ann.width * 100}%`}
                            height={`${ann.height * 100}%`}
                            transform="translate(-50%, -50%)"
                            border="2px solid"
                            borderColor="blue.500"
                            backgroundColor="transparent"
                            pointerEvents="none"
                            zIndex={2}
                          >
                            <Box
                              position="absolute"
                              top="-24px"
                              left="50%"
                              transform="translateX(-50%)"
                              backgroundColor="blue.500"
                              color="white"
                              px="2"
                              py="1"
                              borderRadius="md"
                              fontSize="xs"
                              fontWeight="bold"
                              zIndex={3}
                              whiteSpace="nowrap"
                            >
                              #{idx+1} {features.find(f => f.id === ann.class)?.label || ann.class}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {loadedImageUrl === currentImage.url && imageSize.width > 0 && (
                      <Box position="absolute" inset="0">
                        <BoundingBoxDrawer
                          imageDisplaySize={{
                            width: imageSize.width,
                            height: imageSize.height
                          }}
                          initialBoxes={currentImage.annotations?.map(ann => ({
                            x: ann.x * imageSize.naturalWidth * (imageSize.width / imageSize.naturalWidth),
                            y: ann.y * imageSize.naturalHeight * (imageSize.height / imageSize.naturalHeight),
                            width: ann.width * imageSize.naturalWidth * (imageSize.width / imageSize.naturalWidth),
                            height: ann.height * imageSize.naturalHeight * (imageSize.height / imageSize.naturalHeight)
                          })) || []}
                          onBoxDrawn={handleBoxDrawn}
                        />
                      </Box>
                    )}
                  </Box>
                  <VStack flex="0 0 300px" align="stretch" spacing={4}>
                    <Box>
                      <Text fontWeight="bold" mb={2}>Selected Feature</Text>
                      <Select
                        placeholder="Select feature to add"
                        value={selectedFeature?.id || ''}
                        onChange={(e) => {
                          const feature = features.find(f => f.id === e.target.value);
                          setSelectedFeature(feature);
                        }}
                      >
                        {features.map(feature => (
                          <option key={feature.id} value={feature.id}>
                            {feature.label}
                          </option>
                        ))}
                      </Select>
                    </Box>

                    <Divider />

                    <Box>
                      <Text fontWeight="bold" mb={2}>Existing Annotations</Text>
                      {currentImage.annotations?.length === 0 ? (
                        <Text color="gray.500">No annotations yet</Text>
                      ) : (
                        currentImage.annotations?.map((ann, index) => (
                          <HStack key={index} justify="space-between" mb={2}>
                            <Text>
                              {features.find(f => f.id === ann.class)?.label || ann.class}
                            </Text>
                            <IconButton
                              icon={<TrashSimple />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleAnnotationDelete(index)}
                              aria-label="Delete annotation"
                            />
                          </HStack>
                        ))
                      )}
                    </Box>
                  </VStack>
                </Flex>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <ImagePreviewModal
          isOpen={isImagePreviewOpen}
          onClose={onImagePreviewClose}
          imageUrl={previewImage?.url}
          title={previewImage?.filename}
          overlayContent={
            previewImage?.annotations?.length > 0 ? (
              <Box position="absolute" inset="0" pointerEvents="none">
                {previewImage.annotations.map((ann, idx) => (
                  <Box
                    key={idx}
                    position="absolute"
                    top={`${ann.y * 100}%`}
                    left={`${ann.x * 100}%`}
                    width={`${ann.width * 100}%`}
                    height={`${ann.height * 100}%`}
                    transform="translate(-50%, -50%)"
                    border="2px solid"
                    borderColor="blue.500"
                    backgroundColor="transparent"
                    pointerEvents="none"
                    zIndex={2}
                  >
                    <Box
                      position="absolute"
                      top="-24px"
                      left="50%"
                      transform="translateX(-50%)"
                      backgroundColor="blue.500"
                      color="white"
                      px="2"
                      py="1"
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                      zIndex={3}
                      whiteSpace="nowrap"
                    >
                      #{idx+1} {features.find(f => f.id === ann.class)?.label || ann.class}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null
          }
          actions={
            <Button
              leftIcon={<Pencil />}
              onClick={() => {
                setCurrentImage(previewImage);
                onImagePreviewClose();
                onOpen();
              }}
            >
              Edit Annotations
            </Button>
          }
        />

        <AlertDialog
          isOpen={isErrorDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={closeErrorDialog}
          size="xl"
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Download Errors</AlertDialogHeader>
              <AlertDialogBody>
                <Text mb={4}>
                  Some files failed to download. Review the errors below:
                </Text>
                <Box maxH="400px" overflowY="auto">
                  {downloadErrors.map((error, index) => (
                    <Text key={index} color="red.500" fontSize="sm" mb={2}>
                      {error}
                    </Text>
                  ))}
                </Box>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={closeErrorDialog}>
                  Close
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Box>
  );
}
